import { useGame } from "@/contexts/GameContext";
import PlayersStep from "@/components/config/PlayersStep";
import OccasionStep from "@/components/config/OccasionStep";
import DifficultyStep from "@/components/config/DifficultyStep";
import GameModeStep from "@/components/config/GameModeStep";
import NSFWStep from "@/components/config/NSFWStep";
import ThemesStep from "@/components/config/ThemesStep";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, X as CloseIcon, Play } from "lucide-react";
import { useMemo } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Definicje kroków i konfiguracji (jak wyżej)
const allSteps = [
  {
    id: 0,
    title: "Tryb Gry",
    description: "Wybierz główny styl rozgrywki.",
    component: GameModeStep,
  },
  {
    id: 1,
    title: "Gracze",
    description: "Wpisz imiona znajomych.",
    component: PlayersStep,
  },
  {
    id: 2,
    title: "Okazja",
    description: "Na jaką okazję gracie? (możesz wybrać kilka)",
    component: OccasionStep,
  },
  {
    id: 3,
    title: "Poziom trudności",
    description: "Wybierzcie poziom odwagi. (możesz wybrać kilka)",
    component: DifficultyStep,
  },
  {
    id: 4,
    title: "Treści 18+",
    description: "Czy gra ma zawierać pytania dla dorosłych?",
    component: NSFWStep,
  },
  {
    id: 5,
    title: "Motywy tematyczne",
    description: "Wybierz dodatkowe motywy. (możesz wybrać kilka)",
    component: ThemesStep,
  },
];

const stepsConfig = {
  classic: [0, 1, 2, 3, 4, 5],
  never_have_i_ever: [0, 1, 2, 4, 5],
  who_is_most_likely: [0, 1, 2, 4, 5],
  // Dodaj inne tryby, jeśli je masz
};

export default function ConfigScreen() {
  const { state, dispatch } = useGame();

  // Funkcja sprawdzająca, czy krok powinien być pokazany
  const shouldShowStep = (stepId, gameModeId) => {
    // Krok 0 (Tryb Gry) zawsze pokazujemy
    if (stepId === 0) return true;
    // Jeśli tryb nie jest jeszcze wybrany, pokazujemy wszystko (lub tylko graczy)
    if (!gameModeId) return true; // Możesz zmienić na 'return stepId === 1;'

    const allowedIds = stepsConfig[gameModeId];
    return allowedIds ? allowedIds.includes(stepId) : true; // Jeśli tryb nie ma configu, pokaż wszystko
  };

  // Użyj useMemo, aby przefiltrować kroki tylko wtedy, gdy zmieni się tryb gry
  const visibleSteps = useMemo(() => {
    return allSteps.filter((step) =>
      shouldShowStep(step.id, state.settings.gameMode)
    );
  }, [state.settings.gameMode]);

  // Znajdź *aktualny* krok z `allSteps` na podstawie `state.configStep` (który jest 1-based)
  // `state.configStep` powinno teraz reprezentować indeks w `visibleSteps` + 1
  // Musimy znaleźć odpowiedni `id` kroku, który *powinien* być teraz wyświetlony.
  // Zamiast zmieniać `state.configStep`, będziemy zarządzać nim *inteligentniej*.

  // Znajdź aktualny krok w `allSteps`
  const currentMasterStep = allSteps[state.configStep - 1];

  // Jeśli aktualny krok nie jest widoczny (np. po zmianie trybu), przejdź do następnego/poprzedniego widocznego
  // Ta logika może być skomplikowana. Prostsze jest zarządzanie `currentVisibleIndex`.
  // Dla uproszczenia, skupmy się na logice `next` i `prev`.

  // Znajdź aktualny indeks *wśród widocznych kroków*
  const currentVisibleIndex = visibleSteps.findIndex(
    (step) => step.id === currentMasterStep?.id
  );

  // Jeśli `currentVisibleIndex` jest -1, oznacza to, że `state.configStep` wskazuje
  // na krok, który został ukryty. Musimy to naprawić - np. ustawiając na pierwszy krok.
  // To wymagałoby `useEffect`. Na razie załóżmy, że `next/prev` zadbają o to.

  // Jeśli nie ma kroku, to coś jest nie tak (np. stan początkowy)
  if (currentVisibleIndex === -1 && visibleSteps.length > 0) {
    // Próba naprawy - znajdź pierwszy widoczny krok
    // To wymagałoby dispatch, co może być kłopotliwe.
    // Lepiej upewnić się, że `next/prev` zawsze lądują na widocznym.
    console.warn("Aktualny krok jest niewidoczny, wymaga poprawy logiki!");
    // Na razie, weźmy pierwszy widoczny krok jako bieżący.
    // currentStepData = visibleSteps[0];
    // currentVisibleIndex = 0;
    // UWAGA: Potrzebujesz `dispatch`, aby zaktualizować `state.configStep`!
  }

  const currentStepData = visibleSteps[currentVisibleIndex] || visibleSteps[0]; // Fallback
  const CurrentStepComponent = currentStepData.component;

  // Poprawiona walidacja - używamy `currentStepData.id`
  const canProceed = () => {
    if (!currentStepData) return false;
    switch (currentStepData.id) {
      case 0: // GameModeStep
        return (
          state.settings.gameMode !== null && state.settings.gameMode !== ""
        );
      case 1: // PlayersStep
        return state.players.length >= 2; // Lub >= 1 jeśli dopuszczasz grę samemu
      case 2: // OccasionStep
        return state.settings.occasions && state.settings.occasions.length > 0;
      case 3: // DifficultyStep
        return state.settings.difficulty.length > 0;
      case 4: // NSFWStep
        return state.settings.nsfw !== null;
      case 5: // ThemesStep
        return state.settings.themes.length > 0;
      default:
        return false;
    }
  };

  const nextStep = () => {
    const currentIndex = visibleSteps.findIndex(
      (step) => step.id === currentStepData.id
    );
    if (currentIndex < visibleSteps.length - 1) {
      const nextVisibleStep = visibleSteps[currentIndex + 1];
      // Znajdź oryginalny indeks `nextVisibleStep` w `allSteps`
      const masterIndex = allSteps.findIndex(
        (step) => step.id === nextVisibleStep.id
      );
      dispatch({ type: "SET_CONFIG_STEP", payload: masterIndex + 1 });
    }
  };

  const prevStep = () => {
    const currentIndex = visibleSteps.findIndex(
      (step) => step.id === currentStepData.id
    );

    const prevVisibleStep = visibleSteps[currentIndex - 1];
    const masterIndex = allSteps.findIndex(
      (step) => step.id === prevVisibleStep.id
    );
    dispatch({ type: "SET_CONFIG_STEP", payload: masterIndex + 1 });
  };

  const startGame = () => {
    dispatch({ type: "START_GAME" });
  };

  const goToStart = () => {
    dispatch({ type: "SET_SCREEN", payload: "start" });
  };

  const handleNextOrStart = () => {
    const currentIndex = visibleSteps.findIndex(
      (step) => step.id === currentStepData.id
    );
    if (currentIndex === visibleSteps.length - 1) {
      startGame();
    } else {
      nextStep();
    }
  };

  // Aktualny numer kroku i całkowita liczba widocznych kroków
  const currentStepNumber = currentVisibleIndex + 1;
  const totalStepsNumber = visibleSteps.length;

  return (
    <>
      {/* Header */}
      <header className="flex items-center justify-between mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={prevStep}
          className="rounded-full"
          // Wyłącz, jeśli jesteś na pierwszym kroku (lub niech wraca do menu)
          disabled={currentStepNumber === 1}
        >
          <ArrowLeft className="size-6" />
        </Button>
        <span className="font-semibold text-sm uppercase tracking-wider">
          Krok {currentStepNumber} / {totalStepsNumber}{" "}
          {/* Używamy dynamicznych liczb */}
        </span>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <CloseIcon className="size-6" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Powrót do Menu Głównego?</AlertDialogTitle>
              <AlertDialogDescription>
                Czy na pewno chcesz wrócić do menu głównego? Twój postęp
                zostanie zapisany i będziesz mógł/mogła do niego wrócić później.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anuluj</AlertDialogCancel>
              <AlertDialogAction onClick={goToStart}>
                Tak, wróć
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </header>

      <main className="flex-1 space-y-6 pb-24">
        {/* Tytuł i opis */}
        <div className="text-center mb-6 relative z-10">
          <h1 className="text-3xl font-bold mb-2">{currentStepData.title}</h1>
          <p className="text-base font-light max-w-md mx-auto">
            {currentStepData.description}
          </p>
        </div>

        {/* Obszar treści */}
        <div className="relative w-full z-10">
          <CurrentStepComponent />
        </div>
      </main>

      {/* Przycisk dalej */}
      <footer
        className={
          "w-full bg-background py-4 fixed bottom-0 left-0 right-0 z-20 " + // Dodano left-0 i z-20
          "border-t border-border " + // Subtelna linia oddzielająca na górze
          "shadow-[0_-6px_12px_-3px_rgba(0,0,0,0.2),_0_-4px_8px_-4px_rgba(0,0,0,0.05)] " + // Cień na górze
          "dark:shadow-[0_-6px_12px_-3px_rgba(0,0,0,0.8),_0_-4px_8px_-4px_rgba(0,0,0,0.15)]" // Cień dla trybu ciemnego
        }
      >
        <div className="max-w-xs mx-auto w-full flex justify-center items-center">
          <Button
            onClick={handleNextOrStart}
            disabled={!canProceed()}
            size="lg"
          >
            {currentStepNumber === totalStepsNumber ? ( // Sprawdzamy, czy to ostatni WIDOCZNY krok
              <>
                <Play className="size-5 fill-current" />
                Rozpocznij Grę
              </>
            ) : (
              <>
                Dalej <ArrowRight className="size-5" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </>
  );
}
